import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { listsAsOverdue } from '@/domain';
import { useMyQueue } from '@/application/usecases/useKits';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { formatDuration } from '@/application/timer/timerStore';
import { strings } from '@/shared/strings';
import { labToday } from '@/shared/format/instant';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { StageKitTable, type StageFocus } from '@/features/floor/StageKitTable';
import styles from './FinalCheckScreen.module.css';

/**
 * Final Check. Section 5.2.
 *
 * The gate before Ready. Every case passes through here, and only a holder of
 * `finalcheck` can pass or fail one. The list is the stage view for
 * `finalcheck`, and so it is the stage view's table, filters and all; the
 * decision itself is on the case, one tap away.
 */
export function FinalCheckScreen() {
  const navigate = useNavigate();
  /*
   * A page at a time, from the server. Section 10 of the request of
   * 16 September 2026: this asked for the two hundred cap and rendered the lot.
   */
  const paging = useTablePage('finalcheck');
  // The same table as a stage's, origin sorts included, which the server orders
  // the whole list by. Section 2 of the request of 24 September 2026.
  const [sort, setSort] = useState<string | undefined>(undefined);
  // The figure last pressed, which the table is narrowed by. The lab's request of 17 October 2026, item 1.
  const [focus, setFocus] = useState<StageFocus | null>(null);
  const press = (show: StageFocus['show']) => setFocus({ show, at: Date.now() });
  const { data, isPending, isError } = useMyQueue(
    useMemo(
      () => ({
        stage: 'finalcheck' as const,
        page: paging.page,
        pageSize: paging.pageSize,
        ...(sort === undefined ? {} : { sort }),
      }),
      [paging.page, paging.pageSize, sort],
    ),
  );

  const kits = data?.items ?? [];
  /*
   * Every case at the gate, not the page. The heading and the figures answer
   * "how much is waiting on Final Check", which a page of twenty-five cannot.
   * The three counts below are of the page, and are marked as such on screen.
   */
  const total = data?.total ?? 0;

  // Late by the one rule: the due date has passed. The request of 7 October 2026, section 6.
  const today = labToday();
  const overdue = kits.filter((kit) => listsAsOverdue(kit, today)).length;
  const rework = kits.filter((kit) => kit.rework).length;
  const oldest = kits.reduce((max, kit) => Math.max(max, kit.minutesInStage), 0);
  /** Said out loud only while there is more than one page to be wrong about. */
  const pageScope = total > kits.length ? strings.table.onThisPage : null;

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.finalCheck.label}</span>
        <h2>{strings.finalCheck.title}</h2>
        <span className={styles.hint}>
          {total} {total === 1 ? strings.finalCheck.kitSingular : strings.finalCheck.kits}
        </span>
      </div>

      {isPending && <SkeletonRows rows={4} label={strings.queue.loading} />}
      {isError && <p className={styles.hint}>{strings.errors.server}</p>}

      {!isPending && !isError && kits.length > 0 && (
        <div className={styles.stats}>
          <Stat
            label={strings.finalCheck.stats.kits}
            value={String(total)}
            onPress={() => press('all')}
            pressed={focus?.show === 'all'}
          />
          <Stat
            label={strings.finalCheck.stats.overdue}
            value={String(overdue)}
            alert={overdue > 0}
            note={pageScope}
            onPress={() => press('overdue')}
            pressed={focus?.show === 'overdue'}
          />
          <Stat
            label={strings.finalCheck.stats.rework}
            value={String(rework)}
            note={pageScope}
            onPress={() => press('rework')}
            pressed={focus?.show === 'rework'}
          />
          <Stat
            label={strings.finalCheck.stats.oldest}
            value={formatDuration(oldest * 60)}
            note={pageScope}
            onPress={() => press('oldest')}
            pressed={focus?.show === 'oldest'}
          />
        </div>
      )}

      {!isPending && !isError && (
        <StageKitTable
          kits={kits}
          tableKey="finalcheck"
          // When each case arrived at the lab. The request of 7 October 2026, section 4.
          showReceived
          caption={strings.finalCheck.caption}
          emptyHeading={strings.finalCheck.emptyHeading}
          emptyDetail={strings.finalCheck.emptyDetail}
          onRowClick={(kit) => void navigate(`/qc/${kit.id}`)}
          onListChanged={paging.reset}
          onSortParamChange={setSort}
          focus={focus}
          paging={{
            page: paging.page,
            pageSize: paging.pageSize,
            total,
            sizes: PAGE_SIZES,
            onPage: paging.goTo,
            onPageSize: paging.choose,
            label: strings.table.pagesLabel,
          }}
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  alert = false,
  note,
  onPress,
  pressed = false,
}: {
  label: string;
  value: string;
  alert?: boolean;
  /**
   * What the figure is of, when it is not of everything. A count of the page
   * that does not say so is exactly the quiet lie section 10 of the request of
   * 16 September 2026 exists to fix.
   */
  note?: string | null;
  /** Narrows the table to what the figure counts: the lab's request of 17 October 2026, item 1. */
  onPress: () => void;
  pressed?: boolean;
}) {
  return (
    <button type="button" className={`${styles.stat} ${styles.statPress}`} aria-pressed={pressed} onClick={onPress}>
      <div className={styles.l}>{label}</div>
      <div className={`${styles.v} ${alert ? styles.alert : ''}`}>{value}</div>
      {note != null && <div className={styles.scope}>{note}</div>}
    </button>
  );
}
